// Training-day live monitor — admin-only single-pane dashboard the
// trainer keeps open on a side monitor during the May-4 105-attendee
// training. Refreshes every 2s from /api/admin/training-summary.
//
// Design intent: fewer numbers, bigger numbers, color-coded health.
// Anything that needs interpretation belongs in the system-health
// page, not here.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiCall, apiSoft } from "@/lib/api/http";
import { useAuth } from "@/hooks/useAuth";
import { useRuntime } from "@/hooks/useRuntime";

interface TrainingSummary {
  queue_depth_high: number;
  queue_depth_default: number;
  running_count: number;
  queued_count: number;
  failed_last_hour: number;
  alive_worker_dynos: number;
  db_pool_pct: number | null;
  pack_distribution: { pack: string; count: number }[];
  training_mode: string | null;
  attendee_target: number | null;
  workers_per_dyno: number;
  ts: number;
  failure_trend?: Record<
    string,
    { total: number; failed: number; fail_pct: number }
  >;
  slow_running_jobs?: {
    job_id: string;
    user_email: string | null;
    age_minutes: number | null;
    phase: string | null;
  }[];
  top_error_categories?: { category: string; count: number }[];
  active_trainee_count_30m?: number;
}

const POLL_MS = 2000;

function colorForLoad(pct: number): string {
  if (pct < 50) return "#16a34a";
  if (pct < 80) return "#d97706";
  return "#dc2626";
}

function StatTile({
  label,
  value,
  suffix,
  warn,
  hint,
}: {
  label: string;
  value: number | string;
  suffix?: string;
  warn?: "ok" | "warn" | "fail";
  hint?: string;
}): JSX.Element {
  const fg =
    warn === "fail"
      ? "#dc2626"
      : warn === "warn"
        ? "#d97706"
        : warn === "ok"
          ? "#16a34a"
          : "var(--ink)";
  const bg =
    warn === "fail"
      ? "rgba(220,38,38,.06)"
      : warn === "warn"
        ? "rgba(217,119,6,.06)"
        : warn === "ok"
          ? "rgba(22,163,74,.06)"
          : "white";
  return (
    <div
      style={{
        background: bg,
        border: "1px solid var(--bd)",
        borderRadius: 14,
        padding: "18px 20px",
        minWidth: 0,
      }}
    >
      <div
        className="label"
        style={{ color: "var(--ink-3)", fontSize: 11 }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 36,
          fontWeight: 700,
          letterSpacing: -0.6,
          color: fg,
          lineHeight: 1.05,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        {suffix && (
          <span
            style={{
              fontSize: 16,
              fontWeight: 500,
              opacity: 0.7,
              marginLeft: 4,
            }}
          >
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: "var(--ink-3)",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

export function TrainingMonitor(): JSX.Element {
  const auth = useAuth();
  const runtime = useRuntime();
  const [summary, setSummary] = useState<TrainingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [breakerBusy, setBreakerBusy] = useState(false);

  const toggleBreaker = async (engage: boolean): Promise<void> => {
    setBreakerBusy(true);
    try {
      const reason = engage
        ? window.prompt(
            "Reason for pausing new submissions (shown to trainees in the banner):",
            "training paused — fleet recovering",
          )
        : null;
      if (engage && !reason) {
        setBreakerBusy(false);
        return;
      }
      const params = engage
        ? `?engaged=true&reason=${encodeURIComponent(reason!)}&duration_seconds=300`
        : `?engaged=false`;
      await apiCall<unknown>(
        `/api/admin/training/circuit-breaker${params}`,
        { method: "POST" },
      );
    } catch (e) {
      window.alert(
        `Circuit-breaker call failed: ${e instanceof Error ? e.message : "unknown"}`,
      );
    } finally {
      setBreakerBusy(false);
    }
  };

  const forceFailStuckJobs = async (): Promise<void> => {
    if (!window.confirm(
      "Force-fail every queued/running job idle for >10 min? In-flight " +
      "legitimate jobs are NOT touched. Trainees with reaped jobs can re-run.",
    )) return;
    try {
      const out = await apiCall<{ reaped_count: number }>(
        "/api/admin/training/force-fail-stuck-jobs?older_than_seconds=600",
        { method: "POST" },
      );
      window.alert(`Reaped ${out.reaped_count} stuck job(s).`);
    } catch (e) {
      window.alert(
        `Force-fail failed: ${e instanceof Error ? e.message : "unknown"}`,
      );
    }
  };

  useEffect(() => {
    if (paused || !auth.isAdmin) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      const data = await apiSoft<TrainingSummary>(
        "/api/admin/training-summary",
      );
      if (cancelled) return;
      if (data) {
        setSummary(data);
        setError(null);
      } else {
        setError("Could not reach /api/admin/training-summary.");
      }
    };
    void run();
    const id = setInterval(() => void run(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [paused, auth.isAdmin]);

  if (!auth.loading && !auth.isAdmin) {
    return (
      <div className="page">
        <h1>Admin only</h1>
        <p>This page is reserved for training operations.</p>
        <Link to="/" className="btn primary">
          Back home
        </Link>
      </div>
    );
  }

  const dynoCapacity = summary
    ? summary.alive_worker_dynos * summary.workers_per_dyno
    : 0;
  const utilizationPct =
    summary && dynoCapacity > 0
      ? Math.min(100, Math.round((summary.running_count / dynoCapacity) * 100))
      : 0;
  const queueTotal = summary
    ? summary.queue_depth_high + summary.queue_depth_default
    : 0;

  return (
    <div className="page" style={{ maxWidth: 1180 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: -0.4,
              margin: 0,
            }}
          >
            Training monitor
          </h1>
          <p style={{ color: "var(--ink-2)", fontSize: 13, margin: "4px 0 0" }}>
            Live counters, refreshing every {POLL_MS / 1000}s
            {summary?.training_mode && ` · ${summary.training_mode}`}
            {summary?.attendee_target &&
              ` · target ${summary.attendee_target} attendees`}
            {summary && (
              <span style={{ marginLeft: 8, opacity: 0.7 }}>
                last {new Date(summary.ts * 1000).toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {runtime.circuitBreaker.engaged ? (
            <button
              className="btn primary"
              disabled={breakerBusy}
              style={{ background: "#16a34a" }}
              onClick={() => void toggleBreaker(false)}
              title="Release the circuit breaker — trainees can submit new jobs again"
            >
              Resume submissions
            </button>
          ) : (
            <button
              className="btn primary"
              disabled={breakerBusy}
              style={{ background: "#dc2626" }}
              onClick={() => void toggleBreaker(true)}
              title="Pause new job submissions across the fleet (in-flight jobs continue)"
            >
              Pause submissions
            </button>
          )}
          <button
            className="btn ghost"
            onClick={() => void forceFailStuckJobs()}
            title="Force-fail any queued/running job idle for >10 min"
          >
            Reap stuck jobs
          </button>
          <a
            className="btn ghost"
            href="/api/admin/training/jobs.csv?hours=24"
            download
            title="Download CSV of all jobs from the last 24h for post-mortem"
          >
            Download CSV
          </a>
          <button
            className="btn ghost"
            onClick={() => setPaused((p) => !p)}
            title={paused ? "Resume polling" : "Pause polling (e.g. during a deploy)"}
          >
            {paused ? "Resume polling" : "Pause polling"}
          </button>
          <Link to="/admin" className="btn ghost">
            Full admin →
          </Link>
        </div>
      </div>

      {runtime.circuitBreaker.engaged && (
        <div
          role="alert"
          style={{
            padding: "12px 16px",
            background: "rgba(220,38,38,.08)",
            border: "1px solid rgba(220,38,38,.3)",
            borderRadius: 12,
            color: "#7f1d1d",
            marginBottom: 14,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          New submissions are PAUSED.{" "}
          {runtime.circuitBreaker.reason
            ? `Reason: ${runtime.circuitBreaker.reason}.`
            : ""}{" "}
          {runtime.circuitBreaker.expires_at
            ? `Auto-releases at ${new Date(runtime.circuitBreaker.expires_at * 1000).toLocaleTimeString()}.`
            : ""}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            background: "rgba(220,38,38,.08)",
            color: "#7f1d1d",
            border: "1px solid rgba(220,38,38,.2)",
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <StatTile
          label="Concurrent jobs running"
          value={summary?.running_count ?? "—"}
          suffix={
            summary && dynoCapacity > 0 ? `/ ${dynoCapacity} cap` : undefined
          }
          warn={
            !summary
              ? undefined
              : utilizationPct < 60
                ? "ok"
                : utilizationPct < 90
                  ? "warn"
                  : "fail"
          }
          hint={
            summary && dynoCapacity > 0
              ? `${utilizationPct}% utilization`
              : undefined
          }
        />
        <StatTile
          label="Jobs queued (waiting)"
          value={summary?.queued_count ?? "—"}
          warn={
            !summary
              ? undefined
              : queueTotal === 0
                ? "ok"
                : queueTotal < 50
                  ? "warn"
                  : "fail"
          }
          hint={
            summary
              ? `${summary.queue_depth_high} high · ${summary.queue_depth_default} default`
              : undefined
          }
        />
        <StatTile
          label="Worker dynos alive"
          value={summary?.alive_worker_dynos ?? "—"}
          suffix={summary ? `× ${summary.workers_per_dyno} workers` : undefined}
          warn={
            !summary
              ? undefined
              : summary.alive_worker_dynos < 25
                ? "fail"
                : summary.alive_worker_dynos < 30
                  ? "warn"
                  : "ok"
          }
          hint={
            summary
              ? `${dynoCapacity} concurrent capacity`
              : undefined
          }
        />
        <StatTile
          label="Failed (last hour)"
          value={summary?.failed_last_hour ?? "—"}
          warn={
            !summary
              ? undefined
              : summary.failed_last_hour < 3
                ? "ok"
                : summary.failed_last_hour < 10
                  ? "warn"
                  : "fail"
          }
        />
      </section>

      {/* Round-3 enhancement: failure-rate trend gives the "is it
          getting worse?" answer that raw last-hour count can't. */}
      {summary?.failure_trend && (
        <section
          style={{
            background: "white",
            border: "1px solid var(--bd)",
            borderRadius: 14,
            padding: 18,
            marginBottom: 14,
          }}
        >
          <div
            className="label"
            style={{ color: "var(--ink-3)", marginBottom: 10 }}
          >
            Failure-rate trend
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
            }}
          >
            {(["5m", "15m", "60m"] as const).map((window) => {
              const t = summary.failure_trend?.[window];
              if (!t) return null;
              const pctColor =
                t.fail_pct < 5
                  ? "#16a34a"
                  : t.fail_pct < 15
                    ? "#d97706"
                    : "#dc2626";
              return (
                <div
                  key={window}
                  style={{
                    border: "1px solid var(--bd)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    last {window}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: pctColor,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {t.fail_pct.toFixed(1)}%
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      marginTop: 2,
                    }}
                  >
                    {t.failed} of {t.total} terminal
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Round-3 enhancement: slow-but-not-zombie jobs. These are the
          "trainee getting nervous" cohort — surfacing them lets John
          drop into Monitor and check on them BEFORE they DM. */}
      {summary?.slow_running_jobs && summary.slow_running_jobs.length > 0 && (
        <section
          style={{
            background: "rgba(217,119,6,.04)",
            border: "1px solid rgba(217,119,6,.2)",
            borderRadius: 14,
            padding: 18,
            marginBottom: 14,
          }}
        >
          <div
            className="label"
            style={{ color: "#9a5b00", marginBottom: 10 }}
          >
            Jobs running ≥ 12 min ({summary.slow_running_jobs.length})
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {summary.slow_running_jobs.map((j) => (
              <div
                key={j.job_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto",
                  gap: 12,
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <span style={{ color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {j.user_email ?? "—"}
                </span>
                <span style={{ fontWeight: 600 }}>{j.age_minutes ?? "—"} min</span>
                <span style={{ color: "var(--ink-3)" }}>{j.phase ?? "—"}</span>
                <Link
                  to={`/monitor/${j.job_id}`}
                  className="btn ghost sm"
                  style={{ fontSize: 11 }}
                >
                  Open Monitor →
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {/* Round-3 enhancement: active-trainee count + top error
            categories side-by-side so John can correlate engagement
            with failure-pattern signal. */}
        {summary?.active_trainee_count_30m !== undefined && (
          <div
            style={{
              background: "white",
              border: "1px solid var(--bd)",
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div
              className="label"
              style={{ color: "var(--ink-3)", marginBottom: 10 }}
            >
              Active trainees (last 30 min)
            </div>
            <div
              style={{
                fontSize: 36,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color:
                  summary.active_trainee_count_30m > (summary.attendee_target ?? 0) * 0.5
                    ? "#16a34a"
                    : summary.active_trainee_count_30m > 0
                      ? "#d97706"
                      : "var(--ink)",
                lineHeight: 1.05,
              }}
            >
              {summary.active_trainee_count_30m}
              {summary.attendee_target && (
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    opacity: 0.6,
                    marginLeft: 6,
                  }}
                >
                  / {summary.attendee_target}
                </span>
              )}
            </div>
            <div
              style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}
            >
              Distinct emails with at least one job in the last 30 min.
              Helps tell "the room is engaged" from "20 people watching 1 job".
            </div>
          </div>
        )}
        {summary?.top_error_categories && summary.top_error_categories.length > 0 && (
          <div
            style={{
              background: "white",
              border: "1px solid var(--bd)",
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div
              className="label"
              style={{ color: "var(--ink-3)", marginBottom: 10 }}
            >
              Top error categories (last 60 min)
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {summary.top_error_categories.map((c) => (
                <div
                  key={c.category}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                  }}
                >
                  <span style={{ textTransform: "capitalize" }}>
                    {c.category.replace(/_/g, " ")}
                  </span>
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                    }}
                  >
                    {c.count}
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}
            >
              "OAuth expired" pattern across many trainees usually means
              SSO is flaky. "Perms" pattern means the org-prereq check
              is needed first.
            </div>
          </div>
        )}
      </section>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginTop: 14,
        }}
      >
        <div
          style={{
            background: "white",
            border: "1px solid var(--bd)",
            borderRadius: 14,
            padding: 18,
          }}
        >
          <div
            className="label"
            style={{ color: "var(--ink-3)", marginBottom: 10 }}
          >
            DB pool (local dyno)
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              color:
                summary?.db_pool_pct == null
                  ? "var(--ink)"
                  : colorForLoad(summary.db_pool_pct),
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {summary?.db_pool_pct == null
              ? "—"
              : `${summary.db_pool_pct.toFixed(0)}%`}
          </div>
          <div
            style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}
          >
            One dyno's view of its checked-out connections. Across the
            fleet you have a 500-conn ceiling on Premium-9 — at 30 dynos
            × 5 workers × 3 conn each that's 450 max in saturation.
          </div>
        </div>
        <div
          style={{
            background: "white",
            border: "1px solid var(--bd)",
            borderRadius: 14,
            padding: 18,
          }}
        >
          <div
            className="label"
            style={{ color: "var(--ink-3)", marginBottom: 10 }}
          >
            Pack distribution (active jobs)
          </div>
          {!summary?.pack_distribution.length && (
            <div
              style={{
                fontSize: 13,
                color: "var(--ink-2)",
              }}
            >
              No active jobs.
            </div>
          )}
          <div style={{ display: "grid", gap: 6 }}>
            {summary?.pack_distribution.map((p) => (
              <div
                key={p.pack}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span>{p.pack}</span>
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 600,
                  }}
                >
                  {p.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
